import { Fragment, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import s from './Page.module.css';

/** The scrolling content area of a route. */
export function Page({
  children,
  maxWidth,
  fill,
  className,
}: {
  children: ReactNode;
  /** Caps readable pages (Account, Plans, setup forms). Result pages run full width. */
  maxWidth?: number;
  /** For split layouts that manage their own scrolling. */
  fill?: boolean;
  className?: string;
}) {
  if (fill) return <div className={cx(s.fill, className)}>{children}</div>;
  return (
    <div className={s.scroll}>
      <div className={cx(s.page, className)} style={maxWidth ? { maxWidth } : undefined}>
        {children}
      </div>
    </div>
  );
}

export interface Crumb {
  label: ReactNode;
  to?: string;
  params?: Record<string, string>;
  mono?: boolean;
}

export function PageHeader({
  title,
  subtitle,
  crumbs,
  badges,
  meta,
  actions,
  back,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  crumbs?: Crumb[];
  /** Pills beside the title (direction, status). */
  badges?: ReactNode;
  /** The scope line under the title: instrument · timeframe · window… */
  meta?: ReactNode[];
  actions?: ReactNode;
  back?: { to: string; label: string };
  className?: string;
}) {
  return (
    <header className={cx(s.header, className)}>
      {back && (
        <Link to={back.to} className={s.back} aria-label={back.label} title={back.label}>
          <Icon name="arrow_back" size={20} />
        </Link>
      )}
      <div className={s.titleBlock}>
        {crumbs && crumbs.length > 0 && (
          <nav className={s.crumbs} aria-label="Breadcrumb">
            {crumbs.map((c, i) => (
              <Fragment key={i}>
                {i > 0 && <Icon name="chevron_right" size={14} />}
                {c.to ? (
                  <Link to={c.to} params={c.params} className={s.crumbLink}>
                    {c.label}
                  </Link>
                ) : (
                  <span className={c.mono ? 'mono' : undefined}>{c.label}</span>
                )}
              </Fragment>
            ))}
          </nav>
        )}
        <div className={s.titleRow}>
          <h1 className={s.title}>{title}</h1>
          {badges}
        </div>
        {subtitle && <p className={s.subtitle}>{subtitle}</p>}
        {meta && meta.length > 0 && (
          <div className={s.meta}>
            {meta.filter(Boolean).map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </div>
        )}
      </div>
      {actions && <div className={s.actions}>{actions}</div>}
    </header>
  );
}

/** A responsive grid of panels: `cols` is the column template at wide widths. */
export function Grid({ cols, children, className }: { cols: string; children: ReactNode; className?: string }) {
  return (
    <div className={cx(s.grid, className)} style={{ gridTemplateColumns: cols }}>
      {children}
    </div>
  );
}

export function Stack({ gap = 16, children, className }: { gap?: number; children: ReactNode; className?: string }) {
  return (
    <div className={cx(s.stack, className)} style={{ gap }}>
      {children}
    </div>
  );
}

export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx(s.toolbar, className)}>{children}</div>;
}

export function Spacer() {
  return <span className={s.spacer} />;
}

export function Divider({ vertical }: { vertical?: boolean }) {
  return <span className={vertical ? s.vdivider : s.divider} />;
}
